import { assetsPlaceholder } from '../../../../packages/constants/assetsPlaceholder';
import { IMAGE_ENTITY, PLAYER_ENTITY, TEAM_ENTITY } from '../../../../packages/constants/entityTypes';
import { NOT_AVAILABLE } from '../../../../packages/constants/notAvailable';
import { TARGET_BLANK, TARGET_SELF } from '../../../../packages/constants/windowTarget';
import {
  IBaseTeam,
  IFootballConfig,
  INormalizedStandingColumn,
  ITopScorersConfig,
  ITopScorersDataLabelsKeys,
  ITopScorersResponse,
} from '../../../../packages/types';
import { getTeamName, LinkCreationService, removeUndefinedPropertiesFromObject } from '../../../../packages/utils';
import { INormalizedTopScorers } from '../types';

interface INormalizeTopScorerDataArgs {
  item: ITopScorersResponse;
  dataEntityLinks?: IFootballConfig['dataEntityLinks'];
  dataHighlightEntities?: ITopScorersConfig['dataHighlightEntities'];
  dataTeamNameType?: ITopScorersConfig['dataTeamNameType'];
  dataElements: string[];
  displayImage?: ITopScorersConfig['dataImageDisplay'];
}

export const normalizeTopScorer = ({
  item,
  dataEntityLinks,
  dataHighlightEntities,
  dataTeamNameType,
  dataElements,
  displayImage,
}: INormalizeTopScorerDataArgs): INormalizedTopScorers | undefined => {
  if (!item) return;

  const linkService = dataEntityLinks && new LinkCreationService(dataEntityLinks);
  const highlightedPlayer = dataHighlightEntities?.filter((playerId) => playerId.toString() === item.player.id);

  const defaultColumns = [
    {
      value: item?.rank,
      type: 'text',
      css: { mr: '$3', mx: '12px', width: '$3', textAlign: 'center' },
    },
    {
      value: item?.player.urlThumb || assetsPlaceholder.player,
      type: IMAGE_ENTITY,
      alt: item?.player?.name,
      css: { display: displayImage ? 'block' : 'none' },
    },
    {
      value: item.player.name ? item.player.name : NOT_AVAILABLE,
      eventValues: { id: item?.player?.id, name: item?.player?.name },
      link: linkService?.createEntityUrl(String(item?.player?.id), PLAYER_ENTITY, '', item?.player?.slug) ?? '#',
      urlTarget: dataEntityLinks?.configuration?.newWindow ? TARGET_BLANK : TARGET_SELF,
      type: 'link',
      css: { flexGrow: '1' },
      teamName: getTeamName(item.team as IBaseTeam, dataTeamNameType),
      teamId: item.team.id,
      teamLogo: item?.team?.logo || assetsPlaceholder.team,
      teamLink: linkService?.createEntityUrl(String(item?.team?.id), TEAM_ENTITY, '', item?.team?.slug) ?? '#',
    },
    { value: item?.assists ?? '-', type: 'text', css: { width: '$6', textAlign: 'center' } },
    { value: item?.goals ?? '-', type: 'text', css: { width: '$6', textAlign: 'center' } },
    { value: item.minutes ?? '-', type: 'text', css: { width: '$6', textAlign: 'center' } },
    { value: item.penalties ?? '-', type: 'text', css: { width: '$6', textAlign: 'center' } },
    { value: item.missedPenalties ?? '-', type: 'text', css: { width: '$6', textAlign: 'center' } },
    { value: item.played ?? '-', type: 'text', css: { width: '$6', textAlign: 'center' } },
    { value: item.redCards ?? '-', type: 'text', css: { width: '$6', textAlign: 'center' } },
    { value: item.scoredFirst ?? '-', type: 'text', css: { width: '$6', textAlign: 'center' } },
    { value: item.yellowCards ?? '-', type: 'text', css: { width: '$6', textAlign: 'center' } },
  ];

  const columns = [
    {
      value: item?.rank,
      type: 'text',
      css: { mr: '$3', mx: '12px', width: '$3', textAlign: 'center' },
    },
    {
      value: item?.player.urlThumb || assetsPlaceholder.player,
      type: IMAGE_ENTITY,
      alt: item?.player?.name,
      css: { display: displayImage ? 'block' : 'none', marginRight: '2px' },
    },
    {
      value: item.player.name ? item.player.name : NOT_AVAILABLE,
      eventValues: { id: item?.player?.id, name: item?.player?.name },
      link: linkService?.createEntityUrl(String(item?.player?.id), PLAYER_ENTITY, '', item?.player?.slug) ?? '#',
      urlTarget: dataEntityLinks?.configuration?.newWindow ? TARGET_BLANK : TARGET_SELF,
      type: 'link',
      css: { flexGrow: '1' },
      teamName: getTeamName(item.team as IBaseTeam, dataTeamNameType),
      teamId: item.team.id,
      teamLink: linkService?.createEntityUrl(String(item?.team?.id), TEAM_ENTITY, '', item?.team?.slug) ?? '#',
      teamLogo: item?.team?.logo || assetsPlaceholder.team,
    },
  ];

  const css = { textAlign: 'center', width: '$6', mr: '0', mx: '0' };

  dataElements.forEach((element) =>
    element === ITopScorersDataLabelsKeys.YELLOW_CARDS
      ? columns.push({
          value: (item?.yellowCards as number) ?? '-',
          type: 'text',
          css: css,
        })
      : element === ITopScorersDataLabelsKeys.RED_CARDS
      ? columns.push({
          value: (item?.redCards as number) ?? '-',
          type: 'text',
          css: css,
        })
      : element === ITopScorersDataLabelsKeys.ASSISTS
      ? columns.push({
          value: (item?.assists as number) ?? '-',
          type: 'text',
          css: css,
        })
      : element === ITopScorersDataLabelsKeys.GOALS
      ? columns.push({
          value: (item?.goals as number) ?? '-',
          type: 'text',
          css: css,
        })
      : element === ITopScorersDataLabelsKeys.PENALTIES
      ? columns.push({
          value: (item?.penalties as number) ?? '-',
          type: 'text',
          css: css,
        })
      : element === ITopScorersDataLabelsKeys.MISSED_PENALTIES
      ? columns.push({
          value: (item?.missedPenalties as number) ?? '-',
          type: 'text',
          css: css,
        })
      : element === ITopScorersDataLabelsKeys.PLAYED
      ? columns.push({
          value: (item?.played as number) ?? '-',
          type: 'text',
          css: css,
        })
      : element === ITopScorersDataLabelsKeys.SCORED_FIRST
      ? columns.push({
          value: (item?.scoredFirst as number) ?? '-',
          type: 'text',
          css: css,
        })
      : element === ITopScorersDataLabelsKeys.MINUTES
      ? columns.push({
          value: (item?.minutes as number) ?? '-',
          type: 'text',
          css: css,
        })
      : null,
  );

  return removeUndefinedPropertiesFromObject({
    isHighlighted: highlightedPlayer && highlightedPlayer.length > 0,
    desktopColumns: dataElements.length === 0 ? (defaultColumns as INormalizedStandingColumn[]) : (columns as INormalizedStandingColumn[]),
    mobileColumns: dataElements.length === 0 ? (defaultColumns as INormalizedStandingColumn[]) : (columns as INormalizedStandingColumn[]),
  });
};
